import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { ApplicationContext } from 'src/app/models';
import { StoredFileModel } from 'src/app/models/stored-file.model';
import { BrokerSupportArticle, BrokerSupportArticleLinkType, BrokerSupportConfig, BrokerSupportChatbotProgram, BrokerSupportChatbotReference, BrokerSupportLink, BrokerSupportPanel, BrokerSupportResourceCategory, BrokerSupportResourceGroup, BrokerSupportSlide, getEffectiveResourceGroups } from 'src/app/modules/admin/tpo-config/models/broker-support-config.model';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { NotificationService } from 'src/app/services/notification.service';
import { StoredFileService } from 'src/app/services/stored-file.service';
import { DrawerOptions, DrawerService, DrawerSize } from 'src/app/shared/services/drawer.service';
import { DataService } from 'src/app/core/services/data.service';
import { SignalRService } from 'src/app/services/signalr.service';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { BreadcrumbService } from 'src/app/shared/services/bread-crumb.service';

@Component({
  selector: 'tpo-broker-support',
  templateUrl: './broker-support.component.html',
  styleUrls: ['./broker-support.component.scss'],
  standalone: false
})
export class BrokerSupportComponent extends ApplicationContextBoundComponent implements OnInit, OnDestroy {

  private readonly _destroy$ = new Subject<void>();

  config: BrokerSupportConfig = new BrokerSupportConfig();
  bannerImageUrl: string = '';
  myCases: BrokerSupportCase[] = [];

  /**
   * The active sub-view of the support hub. Driven by the route:
   *   /tpo/support             -> 'landing'
   *   /tpo/support/help        -> 'help'
   *   /tpo/support/resources   -> 'resources'
   *   /tpo/support/resources/:slug -> 'resources' with article preselected
   */
  currentView: 'landing' | 'help' | 'resources' = 'landing';

  /** Article currently rendered in the resources preview pane (right side). */
  selectedArticle: BrokerSupportArticle | null = null;
  selectedArticleHtml: SafeHtml | null = null;
  selectedArticleNotFound: boolean = false;
  /** Index (within `enabledSupportPanels`) of the currently active Submit Ticket tab */
  selectedSupportPanelTabIndex: number = 0;

  /** Priority options available to brokers when submitting tickets */
  readonly brokerPriorities: string[] = ['Low', 'Medium', 'High'];
  /** Per-panel selected priority keyed by panel index */
  panelPriority: { [panelIndex: number]: string } = {};

  /** Filter state for My Cases */
  caseStatusFilter: string = 'All';
  caseSearchTerm: string = '';
  readonly caseStatuses: string[] = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];
  /** Free-text filter for the static Resource Center tree */
  resourceSearchTerm: string = '';
  selectedCase: BrokerSupportCase | null = null;
  replyMessage: string = '';

  /** Cached list of enabled support panels with their original config index */
  enabledSupportPanels: { panel: BrokerSupportPanel; originalIndex: number }[] = [];

  /** Per-panel form field values keyed by panel index, then field label */
  panelFormValues: { [panelIndex: number]: { [fieldLabel: string]: string | boolean } } = {};
  /** Per-panel attachments keyed by panel index */
  panelAttachments: { [panelIndex: number]: BrokerSupportAttachment[] } = {};
  /** Per-panel requester info keyed by panel index */
  panelRequesterInfo: { [panelIndex: number]: { requesterName: string; requesterEmail: string } } = {};

  caseReplyAttachments: BrokerSupportAttachment[] = [];
  /** Stores the current user's full name for use in replies */
  private currentUserFullName: string = '';

  selectedChatbotProgram: BrokerSupportChatbotProgram | null = null;
  chatbotQuestion: string = '';
  chatbotSearchWithOverlays: boolean = true;
  chatbotLoading: boolean = false;

  guidelineGuruEnabled: boolean = false;
  guidelineGuruUrl: string = '';

  caseDrawerOptions: DrawerOptions = {
    size: DrawerSize.Large,
    containerWrapperId: null
  };

  get hasSlides(): boolean {
    return (this.config?.slides?.length ?? 0) > 0;
  }

  get resourceCenterLinks(): BrokerSupportLink[] {
    return this.config?.resourceCenterLinks ?? [];
  }

  get resourceCategories(): BrokerSupportResourceCategory[] {
    return this.config?.resourceCategories ?? [];
  }

  get resourceGroups(): BrokerSupportResourceGroup[] {
    return getEffectiveResourceGroups(this.config);
  }

  get hasResourceGroups(): boolean {
    return this.resourceGroups.length > 0;
  }

  get helpDeskLinks(): BrokerSupportLink[] {
    return this.config?.helpDeskLinks ?? [];
  }

  get liveLoanSupportLink(): BrokerSupportLink | null {
    return this.config?.liveLoanSupportLink ?? null;
  }

  /** TrackBy function for *ngFor to prevent unnecessary DOM recreation */
  trackByOriginalIndex(_index: number, entry: { panel: BrokerSupportPanel; originalIndex: number }): number {
    return entry.originalIndex;
  }

  get hasLiveLoanSupportLink(): boolean {
    return !!this.liveLoanSupportLink?.url;
  }

  get liveLoanSupportUrl(): string {
    return this.liveLoanSupportLink?.url ?? '';
  }

  get liveLoanSupportLabel(): string {
    return this.liveLoanSupportLink?.label || 'Live Loan Support';
  }

  get bannerAltText(): string {
    return this.config?.bannerImageAltText || 'Broker Support Banner';
  }

  private currentUserId: string = '';

  constructor(private readonly injector: Injector,
    private readonly _notificationService: NotificationService,
    private readonly _drawerService: DrawerService,
    private readonly _dataService: DataService,
    private readonly _storedFileService: StoredFileService,
    private readonly _configurationService: ConfigurationService,
    private readonly _signalRService: SignalRService,
    private readonly _environmentService: EnvironmentService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _sanitizer: DomSanitizer,
    private readonly _breadcrumbService: BreadcrumbService) {
    super(injector);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnInit(): void {
    this._configurationService.getCompanyConfiguration('SupportTicketingEnabled').subscribe({
      next: config => {
        if (config?.value !== 1) {
          this._router.navigate(['/tpo/dashboard']);
        }
      },
      error: () => {
        this._router.navigate(['/tpo/dashboard']);
      }
    });

    this.applicationContextService.context.subscribe((context: ApplicationContext) => {
      this.config = context?.tpoConfig?.brokerSupportConfig ?? new BrokerSupportConfig();
      this.bannerImageUrl = this.config.bannerImageUrl;
      this.updateEnabledPanels();
      this.initPanelFormState(context);
      this.currentUserId = context?.currentlyLoggedInUser?.userId || '';
      if (this.config.chatbotPrograms?.length > 0 && !this.selectedChatbotProgram) {
        this.selectedChatbotProgram = this.config.chatbotPrograms[0];
      }
      this.guidelineGuruEnabled = this.config?.guidelineGuruEnabled ?? false;
      if (this.guidelineGuruEnabled) {
        this.guidelineGuruUrl = 'https://app.guidelineguru.ai/a/windsor-mortgage-partners/embed/';
      }
      // Re-resolve the currently selected article in case config arrived after the route did.
      if (this.currentView === 'resources') {
        this.resolveArticleFromRoute();
      }
      this.updateBreadcrumb();
    });
    this.loadMyCases();

    // Track the active view from the route. Both data and params can change
    // without the component being recreated (when navigating between the
    // landing / help / resources sub-routes).
    this.refreshFromRoute();
    this._router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.refreshFromRoute());

    // When the support team replies / changes status on one of this broker's
    // tickets the API broadcasts a SupportTicketUpdated event. Refresh the
    // case list so the broker sees the update without having to refresh the page.
    this._signalRService.supportTicketUpdated$
      .pipe(takeUntil(this._destroy$))
      .subscribe(payload => this.handleRealtimeTicketUpdated(payload));
  }

  private handleRealtimeTicketUpdated(payload: any): void {
    // Refresh the case list, then update the open case if it was the one modified.
    this.loadMyCases(cases => {
      if (this.selectedCase && payload?.supportTicketId === this.selectedCase.supportTicketId) {
        const refreshed = cases.find(c => c.supportTicketId === this.selectedCase.supportTicketId);
        if (refreshed) {
          this.selectedCase = refreshed;
          this.loadAttachmentsForCase(refreshed);
        }
      }
    });
  }

  /** Drills the deepest activated child to read view + slug regardless of nesting. */
  private refreshFromRoute(): void {
    let r: ActivatedRoute = this._route;
    while (r.firstChild) r = r.firstChild;
    const view = (r.snapshot.data?.['view'] as string | undefined) || 'landing';
    this.currentView = (view === 'help' || view === 'resources') ? view : 'landing';
    if (this.currentView === 'resources') {
      this.resolveArticleFromRoute(r);
    } else {
      this.selectedArticle = null;
      this.selectedArticleHtml = null;
      this.selectedArticleNotFound = false;
    }
    this.updateBreadcrumb();
  }

  private resolveArticleFromRoute(r?: ActivatedRoute): void {
    const route = r ?? this.deepestRoute();
    const slug = route.snapshot.paramMap.get('slug');
    if (!slug) {
      this.selectedArticle = null;
      this.selectedArticleHtml = null;
      this.selectedArticleNotFound = false;
      return;
    }
    const article = this.findArticleBySlug(slug);
    if (article) {
      this.selectArticle(article, /*navigate*/ false);
    } else {
      this.selectedArticle = null;
      this.selectedArticleHtml = null;
      this.selectedArticleNotFound = true;
    }
  }

  private deepestRoute(): ActivatedRoute {
    let r: ActivatedRoute = this._route;
    while (r.firstChild) r = r.firstChild;
    return r;
  }

  private findArticleBySlug(slug: string): BrokerSupportArticle | null {
    for (const group of this.resourceGroups) {
      for (const cat of group.categories || []) {
        for (const art of cat.articles || []) {
          if (art.linkType === BrokerSupportArticleLinkType.Hosted && art.slug === slug) {
            return art;
          }
        }
      }
    }
    return null;
  }

  /** Navigation helpers used by the landing-page tile buttons. */
  goToHelp(): void {
    this._router.navigate(['/tpo/support/help']);
  }

  goToResources(): void {
    this._router.navigate(['/tpo/support/resources']);
  }

  goToLanding(): void {
    this._router.navigate(['/tpo/support']);
  }

  /** Click handler for a hosted article inside the resources tree: open inline preview. */
  selectArticle(article: BrokerSupportArticle, navigate: boolean = true): void {
    this.selectedArticle = article;
    this.selectedArticleHtml = this._sanitizer.bypassSecurityTrustHtml(article.htmlContent || '');
    this.selectedArticleNotFound = false;
    if (navigate && article.slug) {
      this._router.navigate(['/tpo/support/resources', article.slug]);
    }
  }

  closeArticlePreview(): void {
    this.selectedArticle = null;
    this.selectedArticleHtml = null;
    this.selectedArticleNotFound = false;
    this._router.navigate(['/tpo/support/resources']);
  }

  /** True when the resources view is showing an inline article preview (split layout). */
  get hasResourcesPreview(): boolean {
    return this.currentView === 'resources' && (!!this.selectedArticle || this.selectedArticleNotFound);
  }

  /** True when the selected article actually has body HTML to render.
   *  Used to swap in a friendlier empty state when the configured article
   *  exists but has no `htmlContent` yet, so the right pane doesn't render
   *  as a giant blank card. */
  get selectedArticleHasBody(): boolean {
    const html = this.selectedArticle?.htmlContent;
    if (!html) {
      return false;
    }
    // Strip tags + entities to check if there's any visible text.
    const stripped = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim();
    return stripped.length > 0;
  }

  selectSupportPanelTab(tabIndex: number): void {
    if (tabIndex >= 0 && tabIndex < this.enabledSupportPanels.length) {
      this.selectedSupportPanelTabIndex = tabIndex;
    }
  }

  clearResourceSearch(): void {
    this.resourceSearchTerm = '';
  }

  /**
   * Returns the resource groups filtered by `resourceSearchTerm`.
   * Categories and articles whose title doesn't match are dropped; groups
   * with no remaining categories are dropped entirely. An empty search term
   * returns the full tree unchanged.
   */
  get filteredResourceGroups(): BrokerSupportResourceGroup[] {
    const groups = this.resourceGroups;
    const term = (this.resourceSearchTerm || '').trim().toLowerCase();
    if (!term) {
      return groups;
    }
    const result: BrokerSupportResourceGroup[] = [];
    for (const group of groups) {
      const groupMatches = (group.name || '').toLowerCase().includes(term);
      const filteredCategories = (group.categories || [])
        .map(cat => {
          const categoryMatches = (cat.name || '').toLowerCase().includes(term);
          const filteredArticles = (cat.articles || [])
            .filter(a => groupMatches || categoryMatches || (a.title || '').toLowerCase().includes(term));
          return { ...cat, articles: filteredArticles };
        })
        .filter(cat => groupMatches || (cat.name || '').toLowerCase().includes(term) || cat.articles.length > 0);
      if (filteredCategories.length > 0) {
        result.push({ ...group, categories: filteredCategories });
      }
    }
    return result;
  }

  get hasResourceCenterContent(): boolean {
    return this.hasResourceGroups || this.resourceCenterLinks.length > 0;
  }

  get hasMyCases(): boolean {
    return this.myCases.length > 0;
  }

  get hasNewsContent(): boolean {
    return !!this.config.slideshowEnabled && this.hasSlides;
  }

  get hasSubmitTicketContent(): boolean {
    return this.enabledSupportPanels.length > 0;
  }

  get hasChatbotContent(): boolean {
    return !!this.config.chatbotEnabled;
  }

  /** True when at least one card in the right-side work column has content to render */
  get hasRightColumnContent(): boolean {
    return this.hasNewsContent || this.hasMyCases || this.hasSubmitTicketContent || this.hasChatbotContent;
  }

  /** Bootstrap col class for the Resource Center column (mold-able layout) */
  get resourceCenterColClass(): string {
    if (!this.hasRightColumnContent) {
      return 'col-12';
    }
    return 'col-lg-3';
  }

  /** Bootstrap col class for the right-side work column (mold-able layout) */
  get rightColumnColClass(): string {
    if (!this.hasResourceCenterContent) {
      return 'col-12';
    }
    return 'col-lg-9';
  }

  get chatbotPrograms(): BrokerSupportChatbotProgram[] {
    return this.config?.chatbotPrograms ?? [];
  }

  get chatbotReferences(): BrokerSupportChatbotReference[] {
    return this.config?.chatbotReferences ?? [];
  }

  get chatbotPlaceholder(): string {
    return this.selectedChatbotProgram?.placeholder || 'Enter your question...';
  }

  selectChatbotProgram(program: BrokerSupportChatbotProgram): void {
    this.selectedChatbotProgram = program;
  }

  askChatbot(): void {
    if (!this.chatbotQuestion.trim() || !this.selectedChatbotProgram) return;
    this.chatbotLoading = true;
    setTimeout(() => {
      this.chatbotLoading = false;
      window.open('#', '_blank');
      this._notificationService.showSuccess('Your question has been submitted. Results will open in a new tab.', 'AI Assistant');
    }, 1500);
  }

  openCaseDrawer(caseItem: BrokerSupportCase): void {
    this.selectedCase = caseItem;
    this.loadAttachmentsForCase(caseItem);
    this.linkCaseToSubmitTicketTab(caseItem);
    this._drawerService.show('caseDetailDrawer', 10, `Case #${caseItem.caseNumber}`);
  }

  /**
   * Pre-selects the Submit Ticket tab whose panel title matches the case's
   * category, so when the broker closes the drawer the ticket form is
   * already on the relevant tab for filing a follow-up. Comparison is
   * case-insensitive and ignores whitespace + punctuation, so "Condo Helpdesk"
   * matches a "Condo Help Desk" panel.
   */
  private linkCaseToSubmitTicketTab(caseItem: BrokerSupportCase): void {
    if (!caseItem?.category) return;
    const normalize = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    const target = normalize(caseItem.category);
    if (!target) return;
    const matchIdx = this.enabledSupportPanels.findIndex(
      e => normalize(e.panel.title || '') === target
    );
    if (matchIdx >= 0) {
      this.selectedSupportPanelTabIndex = matchIdx;
    }
  }

  closeCaseDrawer(): void {
    this._drawerService.hide('caseDetailDrawer', 10);
    this.selectedCase = null;
    this.caseReplyAttachments = [];
  }

  sendReply(): void {
    if (!this.replyMessage.trim() || !this.selectedCase) {
      return;
    }
    const ticketId = this.selectedCase.supportTicketId;
    const filesToUpload = this.caseReplyAttachments.map(a => a.file);
    const message = {
      body: this.replyMessage.trim(),
      direction: 'Inbound',
      senderName: this.currentUserFullName || 'You',
      isInternalNote: false
    };
    this._dataService.post(`api/support-tickets/${ticketId}/messages`, message).subscribe(
      (msg: BrokerSupportMessage) => {
        if (this.selectedCase) {
          msg.attachments = [];
          this.selectedCase.messages.push(msg);
        }
        this.replyMessage = '';
        this.caseReplyAttachments = [];
        this._notificationService.showSuccess('Reply sent.', 'Success');

        if (filesToUpload.length > 0) {
          this.uploadFiles(ticketId, msg.supportTicketMessageId, filesToUpload).subscribe(
            (attachments: BrokerSupportTicketAttachment[]) => {
              if (this.selectedCase) {
                const targetMsg = this.selectedCase.messages.find(m => m.supportTicketMessageId === msg.supportTicketMessageId);
                if (targetMsg) {
                  targetMsg.attachments = attachments;
                }
              }
              this._notificationService.showSuccess(`${attachments.length} attachment(s) uploaded.`, 'Attachments');
            },
            () => {
              this._notificationService.showError('Reply sent but some attachments failed to upload.', 'Attachment Error');
            }
          );
        }
      },
      () => {
        this._notificationService.showError('Failed to send reply. Please try again.', 'Error');
      }
    );
  }

  handleCaseReplyFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    this.addCaseReplyFiles(Array.from(input.files));
    input.value = '';
  }

  handleCaseReplyPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items?.length) return;
    const pastedImages: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind !== 'file') continue;
      if (!item.type.startsWith('image/')) continue;
      const blob = item.getAsFile();
      if (!blob) continue;
      const ext = blob.type.split('/')[1] || 'png';
      const filename = `screenshot-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`;
      pastedImages.push(new File([blob], filename, { type: blob.type }));
    }
    if (pastedImages.length === 0) return;
    event.preventDefault();
    this.addCaseReplyFiles(pastedImages);
  }

  private addCaseReplyFiles(files: File[]): void {
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        this.caseReplyAttachments.push({ file, preview: reader.result as string });
      };
      reader.readAsDataURL(file);
    });
  }

  removeCaseReplyAttachment(index: number): void {
    this.caseReplyAttachments.splice(index, 1);
  }

  downloadAttachment(attachment: BrokerSupportTicketAttachment): void {
    this._storedFileService.getStoredFile(attachment.storagePath).subscribe(
      (blob: BlobPart) => {
        const url = window.URL.createObjectURL(blob as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      () => {
        this._notificationService.showError('Failed to download attachment.', 'Error');
      }
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Open': return 'badge-open';
      case 'In Progress': return 'badge-in-progress';
      case 'Resolved': return 'badge-resolved';
      case 'Closed': return 'badge-closed';
      default: return '';
    }
  }

  getSlideLink(slide: BrokerSupportSlide): string | null {
    return slide?.linkUrl || null;
  }

  /** Generic submit for any dynamic support panel */
  submitPanel(panelIndex: number, panel: BrokerSupportPanel, form: NgForm): void {
    if (!this.validateForm(form)) {
      return;
    }
    const formValues = this.panelFormValues[panelIndex] || {};
    const requesterInfo = this.panelRequesterInfo[panelIndex] || { requesterName: '', requesterEmail: '' };
    const attachments = this.panelAttachments[panelIndex] || [];
    const filesToUpload = attachments.map(a => a.file);

    // Find the subject field value using case-insensitive label match
    const subjectKey = Object.keys(formValues).find(k => k.toLowerCase() === 'subject');
    const subject = subjectKey ? (formValues[subjectKey] as string) || '' : '';
    const ticket = {
      subject: subject,
      category: panel.title,
      priority: this.panelPriority[panelIndex] || 'Medium',
      createdByName: requesterInfo.requesterName,
      createdByEmail: requesterInfo.requesterEmail,
      createdByUserId: this.currentUserId
    };
    const messageBody = {
      body: this.buildPanelDescription(panel, formValues),
      direction: 'Inbound',
      senderName: requesterInfo.requesterName,
      isInternalNote: false
    };
    this._dataService.post('api/support-tickets', ticket).pipe(
      switchMap((created: BrokerSupportCase) => {
        return this._dataService.post(
          `api/support-tickets/${created.supportTicketId}/messages`,
          messageBody
        ).pipe(
          map((msg: BrokerSupportMessage) => ({ ticket: created, message: msg }))
        );
      })
    ).subscribe(
      (result: { ticket: BrokerSupportCase; message: BrokerSupportMessage }) => {
        this._notificationService.showSuccess(`${panel.title} request submitted.`, 'Success');
        this.uploadAttachmentsAfterMessageCreated(result.ticket.supportTicketId, result.message.supportTicketMessageId, filesToUpload);
        this.resetPanelForm(panelIndex, panel, form);
        this.loadMyCases();
      },
      () => {
        this._notificationService.showError('Failed to submit request. Please try again.', 'Error');
      }
    );
  }

  private validateForm(form: NgForm): boolean {
    if (!form.valid) {
      form.control.markAllAsTouched();
      this._notificationService.showError('Please complete all required fields.', 'Validation');
      return false;
    }
    return true;
  }

  handlePanelFileSelect(event: Event, panelIndex: number): void {
    const files = (event instanceof DragEvent)
      ? event.dataTransfer?.files
      : (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;
    if (!this.panelAttachments[panelIndex]) {
      this.panelAttachments[panelIndex] = [];
    }
    const attachments = this.panelAttachments[panelIndex];
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        attachments.push({ file, preview: reader.result as string });
      };
      reader.readAsDataURL(file);
    });
    if (!(event instanceof DragEvent)) {
      (event.target as HTMLInputElement).value = '';
    }
  }

  removePanelAttachment(panelIndex: number, index: number): void {
    if (this.panelAttachments[panelIndex]) {
      this.panelAttachments[panelIndex].splice(index, 1);
    }
  }

  getPanelAttachments(panelIndex: number): BrokerSupportAttachment[] {
    return this.panelAttachments[panelIndex] || [];
  }

  /** Returns select options for a field (prepends --None-- for non-required fields) */
  getFieldSelectOptions(field: { label: string; type: string; required: boolean; options: string[] }): SelectOption[] {
    const opts: SelectOption[] = field.required ? [] : [{ label: '--None--', value: '' }];
    return opts.concat((field.options || []).map(o => ({ label: o, value: o })));
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }

  private resetPanelForm(panelIndex: number, panel: BrokerSupportPanel, form: NgForm): void {
    // Reset field values but preserve requester info
    const formValues = this.panelFormValues[panelIndex] || {};
    for (const field of panel.fields) {
      if (field.type === 'checkbox') {
        formValues[field.label] = false;
      } else {
        formValues[field.label] = '';
      }
    }
    this.panelPriority[panelIndex] = 'Medium';
    this.panelAttachments[panelIndex] = [];
    form.resetForm();
  }

  /** Returns cases filtered by status and search term */
  get filteredCases(): BrokerSupportCase[] {
    return this.myCases.filter(c => {
      const matchesStatus = this.caseStatusFilter === 'All' || c.status === this.caseStatusFilter;
      const matchesSearch = !this.caseSearchTerm ||
        c.subject.toLowerCase().includes(this.caseSearchTerm.toLowerCase()) ||
        c.caseNumber.toLowerCase().includes(this.caseSearchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }

  /** Returns the count of cases for a given status filter value */
  getCaseStatusCount(status: string): number {
    if (status === 'All') return this.myCases.length;
    return this.myCases.filter(c => c.status === status).length;
  }

  private loadMyCases(onLoaded?: (cases: BrokerSupportCase[]) => void): void {
    if (!this.currentUserId) {
      this.myCases = [];
      onLoaded?.(this.myCases);
      return;
    }
    this._dataService.get(`api/support-tickets?createdByUserId=${encodeURIComponent(this.currentUserId)}`).subscribe(
      (tickets: BrokerSupportCase[]) => {
        this.myCases = (tickets || []).filter(t => !t.isArchived);
        onLoaded?.(this.myCases);
      },
      () => {
        this.myCases = [];
        onLoaded?.(this.myCases);
      }
    );
  }

  /** Builds the ticket message body from all dynamic field values */
  private buildPanelDescription(panel: BrokerSupportPanel, formValues: { [key: string]: string | boolean }): string {
    const parts: string[] = [];
    for (const field of panel.fields) {
      const val = formValues[field.label];
      // Skip Subject since it's used as the ticket subject, not in the body
      if (field.label.toLowerCase() === 'subject') {
        continue;
      }
      if (field.type === 'checkbox') {
        parts.push(`${field.label}: ${val ? 'Yes' : 'No'}`);
      } else if (val && typeof val === 'string' && val.trim()) {
        parts.push(`${field.label}: ${val}`);
      }
    }
    return parts.join('\n');
  }

  private uploadAttachmentsAfterMessageCreated(ticketId: number, messageId: number, files: File[]): void {
    if (!files || files.length === 0) return;
    this.uploadFiles(ticketId, messageId, files).subscribe(
      (attachments: BrokerSupportTicketAttachment[]) => {
        this._notificationService.showSuccess(`${attachments.length} attachment(s) uploaded.`, 'Attachments');
      },
      () => {
        this._notificationService.showError('Ticket created but some attachments failed to upload.', 'Attachment Error');
      }
    );
  }

  private uploadSingleFile(ticketId: number, messageId: number, file: File): Observable<BrokerSupportTicketAttachment> {
    return new Observable<string>(subscriber => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        subscriber.next(base64);
        subscriber.complete();
      };
      reader.onerror = () => subscriber.error(reader.error);
      reader.readAsDataURL(file);
    }).pipe(
      switchMap((base64Data: string) => {
        return this._storedFileService.storedFileUpload({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          base64FileData: base64Data
        });
      }),
      switchMap((storedFile: StoredFileModel) => {
        const attachment = {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
          storagePath: storedFile.guid
        };
        return this._dataService.post(
          `api/support-tickets/${ticketId}/messages/${messageId}/attachments`,
          attachment
        );
      })
    );
  }

  private uploadFiles(ticketId: number, messageId: number, files: File[]): Observable<BrokerSupportTicketAttachment[]> {
    if (!files || files.length === 0) {
      return of([]);
    }
    const uploads = files.map(file => this.uploadSingleFile(ticketId, messageId, file));
    return forkJoin(uploads);
  }

  private loadAttachmentsForCase(caseItem: BrokerSupportCase): void {
    if (!caseItem.messages || caseItem.messages.length === 0) return;
    caseItem.messages.forEach(msg => {
      this._dataService.get(`api/support-tickets/${caseItem.supportTicketId}/messages/${msg.supportTicketMessageId}/attachments`).subscribe(
        (attachments: BrokerSupportTicketAttachment[]) => {
          msg.attachments = attachments;
        },
        () => {
          msg.attachments = [];
        }
      );
    });
  }

  /** Overrides the breadcrumb with the configured page title */
  private updateBreadcrumb(): void {
    const title = this.config?.pageTitle || 'Broker Support';
    setTimeout(() => {
      this._breadcrumbService.overrideWith([
        { label: 'TPO', url: '/tpo/dashboard' },
        { label: title, url: '/tpo/support' }
      ]);
    });
  }

  /** Recomputes the cached list of enabled support panels */
  private updateEnabledPanels(): void {
    this.enabledSupportPanels = (this.config?.supportPanels ?? [])
      .map((panel, idx) => ({ panel, originalIndex: idx }))
      .filter(entry => entry.panel.enabled);
    // Clamp the active tab index so it always points at a valid panel.
    if (this.selectedSupportPanelTabIndex >= this.enabledSupportPanels.length) {
      this.selectedSupportPanelTabIndex = 0;
    }
  }

  /** Initializes per-panel form state and requester defaults from user context */
  private initPanelFormState(context: ApplicationContext): void {
    const profile = context?.currentlyLoggedInUserProfile?.userProfile;
    const user = context?.currentlyLoggedInUser;
    const fullName = profile ? `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() : '';
    const email = profile?.email || user?.email || '';
    this.currentUserFullName = fullName;

    const panels = this.config?.supportPanels ?? [];
    panels.forEach((panel, idx) => {
      // Initialize form values if not already set
      if (!this.panelFormValues[idx]) {
        this.panelFormValues[idx] = {};
        for (const field of panel.fields) {
          this.panelFormValues[idx][field.label] = field.type === 'checkbox' ? false : '';
        }
      }
      // Initialize priority selection
      if (!this.panelPriority[idx]) {
        this.panelPriority[idx] = 'Medium';
      }
      // Initialize attachments
      if (!this.panelAttachments[idx]) {
        this.panelAttachments[idx] = [];
      }
      // Initialize requester info
      if (!this.panelRequesterInfo[idx]) {
        this.panelRequesterInfo[idx] = { requesterName: fullName, requesterEmail: email };
      }
    });
  }
}

class BrokerSupportCase {
  supportTicketId: number = 0;
  caseNumber: string = '';
  subject: string = '';
  status: string = '';
  category: string = '';
  dateInserted: Date = new Date();
  isArchived: boolean = false;
  messages: BrokerSupportMessage[] = [];
}

interface BrokerSupportMessage {
  supportTicketMessageId: number;
  supportTicketId: number;
  body: string;
  direction: 'Inbound' | 'Outbound';
  senderName: string;
  isInternalNote: boolean;
  dateInserted: Date;
  attachments?: BrokerSupportTicketAttachment[];
}

interface BrokerSupportTicketAttachment {
  supportTicketAttachmentId: number;
  supportTicketMessageId: number;
  fileName: string;
  mimeType: string;
  fileSize: number | null;
  storagePath: string;
}

interface BrokerSupportAttachment {
  file: File;
  preview: string;
}

interface SelectOption {
  label: string;
  value: string;
}
